import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getPosts, deletePost } from '../../api/news';
import { useAuth } from '../../context/AuthContext';
import NewsItem from './NewsItem';
import NewsPostDialog from './NewsPostDialog';

const NewsList = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const collectionId = searchParams.get('collection');
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedRole, setSelectedRole] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  
  const pageSize = 10;
  const isAdmin = user?.is_staff || user?.is_superuser;

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize
      };
      if (isAdmin && selectedRole) {
        params.roleId = selectedRole;
      }
      const result = await getPosts(params);
      setPosts(result.items);
      setTotal(result.total);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load news posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page, selectedRole]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
    setPage(1); // Reset page when filter changes
  };

  const handleAddPost = () => {
    setCurrentPost(null);
    setDialogOpen(true);
  };

  const handleEditPost = (post) => {
    setCurrentPost(post);
    setDialogOpen(true);
  };

  const handleDeletePost = async (post) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deletePost(post.id);
        fetchPosts();
      } catch (err) {
        console.error('Error deleting post:', err);
        setError('Failed to delete post');
      }
    }
  };

  const handleDialogClose = (refresh = false) => {
    setDialogOpen(false);
    if (refresh) {
      fetchPosts();
    }
  };

  // Helper function for generating pagination buttons
  const renderPagination = () => {
    const totalPages = Math.ceil(total / pageSize);
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center mt-4">
        <nav>
          <ul className="flex gap-2">
            {Array.from({ length: totalPages }, (_, idx) => {
              const currentPage = idx + 1;
              return (
                <li key={currentPage}>
                  <button
                    type="button"
                    onClick={(e) => handlePageChange(e, currentPage)}
                    className={`px-3 py-1 border rounded-md transition-colors ${page === currentPage 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white text-primary-600 hover:bg-gray-100 dark:bg-ui-dark-card dark:text-ui-dark-text-primary'
                      }`}
                  >
                    {currentPage}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
          News & Announcements
        </h1>
        <div className="flex items-center mt-4 sm:mt-0">
          {isAdmin && (
            <div className="mr-4">
              <label htmlFor="role-filter" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-1">
                Filter by Role
              </label>
              <select
                id="role-filter"
                value={selectedRole}
                onChange={handleRoleChange}
                className="px-3 py-2 border rounded-md bg-white dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary"
              >
                <option value="">All Roles</option>
                {user?.available_roles?.map(role => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={handleAddPost}
              className="flex items-center bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Post
            </button>
          )}
        </div>
      </div>

      {/* Loading Spinner */}
      {loading && (
        <div className="flex justify-center my-6">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 rounded-md bg-status-error-light-bg dark:bg-status-error-dark-bg text-status-error-light-text dark:text-status-error-dark-text">
          {error}
        </div>
      )}

      {/* No Posts Alert */}
      {!loading && !error && posts.length === 0 && (
        <div className="mb-6 p-4 rounded-md bg-primary-600 text-white px">
          No news posts available.
        </div>
      )}

      {/* Posts List */}
      <div className="space-y-4">
        {posts.map(post => (
          <NewsItem 
            key={post.id}
            post={post}
            isPreview={true}
            canEdit={isAdmin}
            onEdit={handleEditPost}
            onDelete={handleDeletePost}
          />
        ))}
      </div>

      {/* Pagination */}
      {total > pageSize && renderPagination()}

      {/* News Post Dialog */}
      <NewsPostDialog 
        open={dialogOpen}
        onClose={handleDialogClose}
        post={currentPost}
      />
    </div>
  );
};

export default NewsList;